import React, { useCallback } from "react";
import Modal from "./components/Modal";
import articleService from "../../../service/article.service";
import StatusHandler from "../../../components/articles/StatusHandler";
import { useArticleManagement } from "../../../hooks/useArticleManagement";
import { useArticleNavigation } from "../../../hooks/useArticleNavigation";
import ArticleSearch from "../../../components/articles/ArticleSearch";
import ArticleTable from "../../../components/articles/ArticleTable";
import Pagination from "../../../components/articles/Pagination";
import "react-quill/dist/quill.snow.css";
import useUserRole from "./../../../utils/useUserRole";

const ArticlesPartage = () => {
  // Hook de navigation réutilisable
  const { navigateToDetails } = useArticleNavigation();
  const userRole = useUserRole();

  // Utilisation du hook partagé avec navigation personnalisée
  const {
    isLoading,
    error,
    searchTerm,
    currentItems,
    handleSearch,
    getArticleTitle,
    getArticleAuthor,
    getArticleDate,
    fetchArticles,
    currentPage,
    handlePageChange,
    itemsPerPage,
    handleItemsPerPageChange,
    indexOfFirstItem,
    indexOfLastItem,
    totalItems,
    totalPages,
    isModalOpen,
    setIsModalOpen,
    modalTitle,
    setModalTitle,
    modalSize,
    setModalSize,
    modalContent,
  } = useArticleManagement(() => articleService.getAllPartage(100), null, {
    enableModal: true,
    enableEdit: false,
    customNavigationHandler: navigateToDetails,
  });

  // Formater une date ISO
  const formatDate = useCallback((dateString) => {
    if (!dateString) return "Non spécifiée";
    const date = new Date(dateString);
    return date.toLocaleDateString("fr-FR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  }, []);

  // Obtenir les noms des évaluateurs
  const getEvaluateursNames = useCallback((article) => {
    if (!article.reviewers || article.reviewers.length === 0) {
      return "Aucun évaluateur assigné";
    }
    return article.reviewers
      .map(
        (reviewer) =>
          `${reviewer.grade_rev || ""} ${reviewer.first_name || ""} ${reviewer.last_name || ""}`
      )
      .join(", ");
  }, []);

  // Callback de mise à jour de statut
  const handleStatusUpdate = useCallback(async ({ article, newStatus, comment }) => {
    console.log("🔄 Callback mise à jour statut article partagé:", {
      articleId: article.id_art,
      newStatus,
      commentLength: comment.length,
      user: "HiQaody",
      timestamp: "2025-08-31 07:33:50",
    });

    try {
      await fetchArticles();
      setIsModalOpen(false);
      
      console.log("✅ Articles partagés rechargés après changement statut:", {
        newTotalItems: totalItems,
        user: "HiQaody",
        timestamp: "2025-08-31 07:33:50",
      });
    } catch (error) {
      console.error("❌ Erreur lors du rechargement des articles partagés:", {
        error: error.message,
        user: "HiQaody",
        timestamp: "2025-08-31 07:33:50",
      });
    }
  }, [fetchArticles, setIsModalOpen, totalItems]);

  // Configuration personnalisée pour ArticlesPartage
  const customStatusFilter = useCallback((status) => {
    // Exclure les statuts "publié" et "en révision"
    return status.id_stat !== 5 && status.id_stat !== 4;
  }, []);

  // Règles d'avertissement spécifiques pour ArticlesPartage (articles en évaluation)
  const warningRules = {
    firstEvaluation: {
      condition: (orig, new_stat) => 
        (orig === '8' || orig === 8) && (new_stat !== '8' && new_stat !== 8),
      title: '🔍 Première évaluation',
      description: 'Vous effectuez la première évaluation de cet article partagé.',
      config: {
        title: '🔍 Première évaluation d\'article',
        text: 'Vous êtes sur le point d\'effectuer la première évaluation de cet article. Cette décision orientera son parcours de publication.',
        icon: 'info',
        confirmButtonText: 'Confirmer l\'évaluation'
      }
    },
    toPublishable: {
      condition: (orig, new_stat) => new_stat === '3' || new_stat === 3,
      config: {
        title: '🚀 Article accepté pour publication',
        text: 'Vous marquez cet article comme publiable. Il sera prêt pour la publication après validation finale.',
        icon: 'success'
      }
    },
    toRevision: {
      condition: (orig, new_stat) => new_stat === '2' || new_stat === 2,
      config: {
        title: '📝 Demande de révision',
        text: 'Vous demandez à l\'auteur de remanier son article selon vos recommandations.',
        icon: 'warning'
      }
    },
    toReject: {
      condition: (orig, new_stat) => new_stat === '7' || new_stat === 7,
      config: {
        title: '❌ Refus de l\'article',
        text: 'Vous refusez cet article. L\'auteur en sera informé avec vos commentaires.',
        icon: 'error'
      }
    },
    backToEvaluation: {
      condition: (orig, new_stat) => 
        (orig !== '8' && orig !== 8) && (new_stat === '8' || new_stat === 8),
      title: '🔄 Retour en évaluation',
      description: 'Vous remettez cet article en cours d\'évaluation.',
      config: {
        title: '🔄 Retour en évaluation',
        text: 'Vous remettez cet article en cours d\'évaluation. Il sera à nouveau disponible pour révision.',
        icon: 'info'
      }
    }
  };

  // Suggestions de commentaires pour ArticlesPartage
  const suggestionComments = [
    "Article bien structuré, méthodologie solide - Recommandé pour publication",
    "Travail de qualité avec quelques ajustements mineurs nécessaires",
    "Résultats intéressants mais nécessite des révisions sur la discussion",
    "Méthodologie à clarifier avant publication",
    "Article prometteur mais conclusion à renforcer",
    "Données solides, présentation à améliorer",
    "Contribution significative au domaine - Accepté",
    "Références bibliographiques insuffisantes pour le niveau requis",
    "Analyse statistique à compléter",
    "Innovation méthodologique remarquable"
  ];

  // Colonnes personnalisées pour afficher les évaluateurs et les dates
  const customColumns = [
    {
      header: "Évaluateurs",
      render: (article) => (
        <div className="text-sm text-gray-600">
          {getEvaluateursNames(article)}
        </div>
      ),
    },
    {
      header: "Date de soumission",
      render: (article) => (
        <div className="text-sm text-gray-600">
          {formatDate(article.date_soum_art)}
        </div>
      ),
    },
    {
      header: "Date d'évaluation",
      render: (article) => (
        <div className="text-sm text-gray-600">
          {article.date_eval_art
            ? formatDate(article.date_eval_art)
            : "Non évalué"}
        </div>
      ),
    },
  ];

  // État de chargement
  if (isLoading) {
    console.log("⏳ Chargement des articles partagés:", {
      user: "HiQaody",
      timestamp: "2025-08-31 07:33:50",
    });
    
    return (
      <div className="flex h-96 items-center justify-center">
        <div className="h-12 w-12 animate-spin rounded-full border-b-2 border-t-2 border-[#57A863]"></div>
      </div>
    );
  }

  // État d'erreur
  if (error) {
    console.error("❌ Erreur lors du chargement des articles partagés:", {
      error,
      user: "HiQaody",
      timestamp: "2025-08-31 07:33:50",
    });
    
    return (
      <div className="rounded-lg bg-red-50 p-4 text-center text-red-800">
        <p>{error}</p>
        <button
          onClick={fetchArticles}
          className="mt-4 rounded-md bg-red-600 px-4 py-2 text-white hover:bg-red-700"
        >
          Réessayer
        </button>
      </div>
    );
  }

  console.log("🖼️ Rendu ArticlesPartage:", {
    totalItems,
    currentPage,
    userRole,
    hasItems: currentItems.length > 0,
    user: "HiQaody",
    timestamp: "2025-08-31 07:33:50",
  });

  return (
    <div className="container mx-auto px-4 py-6">
      <ArticleSearch
        searchTerm={searchTerm}
        handleSearch={handleSearch}
        title="Articles en cours d'évaluation"
        subtitle="Liste des articles récemment soumis en attente d'attribution des status"
      />

      <StatusHandler
        onStatusUpdate={handleStatusUpdate}
        showConfirmation={true}
        customStatusFilter={customStatusFilter}
        warningRules={warningRules}
        minimumCommentLength={20} // Plus strict pour l'évaluation
        suggestionComments={suggestionComments}
      >
        {({ 
          initializeStatusAttribution, 
          selectedArticle: statusSelectedArticle,
          StatusSelector,
          CommentInput, 
          StatusMessages,
          handleSubmitStatus,
          isValid,
          isSubmitting: statusIsSubmitting 
        }) => (
          <>
            <div className="mb-6 overflow-hidden rounded-lg">
              <ArticleTable
                currentItems={currentItems}
                getArticleTitle={getArticleTitle}
                getArticleAuthor={getArticleAuthor}
                getArticleDate={getArticleDate}
                handleActionPrimary={userRole === "Administrateur" ? undefined : (article) => {
                  console.log("🎯 Attribution statut initiée pour article partagé:", {
                    articleId: article.id_art,
                    currentStatus: article.id_stat,
                    title: getArticleTitle(article),
                    evaluators: getEvaluateursNames(article),
                    user: "HiQaody",
                    timestamp: "2025-08-31 07:33:50",
                  });
                  initializeStatusAttribution(article);
                  setModalTitle("Attribuer des status");
                  setModalSize("2xl");
                  setIsModalOpen(true);
                }}
                handleViewDetails={navigateToDetails}
                showEditButton={false}
                showPrimaryButton={false}
                showStatusButton={userRole !== "Administrateur"}
                handleStatusArticle={(article) => {
                  console.log("📊 Ouverture modal statut depuis bouton:", {
                    articleId: article.id_art,
                    user: "HiQaody",
                    timestamp: "2025-08-31 07:33:50",
                  });
                  initializeStatusAttribution(article);
                  setModalTitle("Attribuer des status");
                  setModalSize("2xl");
                  setIsModalOpen(true);
                }}
                actionPrimaryText={userRole === "Administrateur" ? null : "Attribuer"}
                handleReloadData={fetchArticles}
                customColumns={customColumns}
                dateColumnLabel="date d'évaluation"
              />

              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                handlePageChange={handlePageChange}
                itemsPerPage={itemsPerPage}
                handleItemsPerPageChange={handleItemsPerPageChange}
                indexOfFirstItem={indexOfFirstItem}
                indexOfLastItem={indexOfLastItem}
                totalItems={totalItems}
              />
            </div>

            {/* Modal avec StatusHandler */}
            <Modal
              isOpen={isModalOpen}
              onClose={() => {
                console.log("🔄 Fermeture modal articles partagés:", {
                  user: "HiQaody",
                  timestamp: "2025-08-31 07:33:50",
                });
                setIsModalOpen(false);
              }}
              title={modalTitle}
              width={modalSize}
            >
              {modalTitle === "Attribuer des status" && statusSelectedArticle ? (
                <div className="bg-white">
                  {/* En-tête de la modal avec informations de l'article */}
                  <div className="border-b border-gray-200 px-6 py-4">
                    <div className="flex items-start space-x-4">
                      <div className="flex-shrink-0">
                        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-100">
                          <svg
                            className="h-5 w-5 text-blue-600"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth="2"
                              d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                            />
                          </svg>
                        </div>
                      </div>
                      <div className="min-w-0 flex-1">
                        <h3 className="truncate text-lg font-semibold text-gray-900">
                          {getArticleTitle(statusSelectedArticle)}
                        </h3>
                        <div className="mt-1 flex items-center space-x-4 text-sm text-gray-500">
                          <span>Par {getArticleAuthor(statusSelectedArticle)}</span>
                          <span>•</span>
                          <span>
                            Soumis le {formatDate(statusSelectedArticle?.date_soum_art)}
                          </span>
                          <span>•</span>
                          <span className="inline-flex items-center rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-800">
                            En évaluation
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Informations sur les évaluateurs */}
                  {statusSelectedArticle.reviewers && statusSelectedArticle.reviewers.length > 0 && (
                    <div className="border-b border-gray-100 bg-blue-50 px-6 py-3">
                      <div className="flex items-center space-x-2 text-sm text-blue-800">
                        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                        </svg>
                        <span className="font-medium">Évaluateurs assignés:</span>
                        <span>{getEvaluateursNames(statusSelectedArticle)}</span>
                      </div>
                    </div>
                  )}

                  {/* Corps de la modal */}
                  <div className="space-y-6 px-6 py-6">
                    {statusSelectedArticle.id_stat !== 5 ? (
                      <>
                        {/* Information contextuelle pour l'évaluation */}
                        <div className="rounded-lg border border-blue-200 bg-blue-50 p-4">
                          <div className="flex items-start space-x-3">
                            <div className="flex-shrink-0">
                              <svg
                                className="mt-0.5 h-5 w-5 text-blue-600"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth="2"
                                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                                />
                              </svg>
                            </div>
                            <div className="flex-1">
                              <h5 className="text-sm font-medium text-blue-900">
                                Évaluation d'article en cours
                              </h5>
                              <p className="mt-1 text-sm text-blue-700">
                                Cet article est en cours d'évaluation. Votre décision déterminera 
                                la suite du processus de publication. Veuillez fournir des 
                                commentaires détaillés pour justifier votre choix.
                              </p>
                            </div>
                          </div>
                        </div>

                        {/* Sélection du nouveau statut */}
                        {StatusSelector}

                        {/* Section commentaire avec exigence renforcée */}
                        <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 mb-4">
                          <div className="flex items-center space-x-2 text-sm text-amber-800">
                            <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                            </svg>
                            <span className="font-medium">
                              Commentaire d'évaluation requis (minimum 20 caractères)
                            </span>
                          </div>
                        </div>

                        {CommentInput}
                      </>
                    ) : (
                      // Article publié (cas rare)
                      <div className="rounded-lg border border-green-200 bg-green-50 p-4">
                        <div className="flex items-start space-x-3">
                          <div className="flex-shrink-0">
                            <svg
                              className="mt-0.5 h-5 w-5 text-green-600"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth="2"
                                d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                              />
                            </svg>
                          </div>
                          <div className="flex-1">
                            <h5 className="text-sm font-medium text-green-900">
                              Article déjà publié
                            </h5>
                            <p className="mt-1 text-sm text-green-700">
                              Cet article est déjà publié et ne peut plus changer de statut.
                            </p>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Messages d'erreur et de succès */}
                    {StatusMessages}
                  </div>

                  {/* Footer avec boutons */}
                  <div className="flex justify-end gap-3 border-t bg-gray-50 px-6 py-4">
                    <button
                      className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50"
                      onClick={() => {
                        console.log("❌ Annulation évaluation article:", {
                          articleId: statusSelectedArticle.id_art,
                          user: "HiQaody",
                          timestamp: "2025-08-31 07:33:50",
                        });
                        setIsModalOpen(false);
                      }}
                      disabled={statusIsSubmitting}
                    >
                      Annuler
                    </button>
                    
                    {statusSelectedArticle.id_stat !== 5 && (
                      <button
                        className={`rounded-md px-6 py-2 text-sm font-medium text-white shadow-sm transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                          isValid && !statusIsSubmitting
                            ? "bg-[#57A863] hover:bg-[#4a9154] focus:ring-[#57A863] shadow-lg hover:shadow-xl"
                            : "cursor-not-allowed bg-gray-400 focus:ring-gray-400"
                        }`}
                        onClick={() => {
                          console.log("🚀 Soumission évaluation article:", {
                            articleId: statusSelectedArticle.id_art,
                            newStatus: statusSelectedArticle.id_stat,
                            isValid,
                            evaluators: getEvaluateursNames(statusSelectedArticle),
                            user: "HiQaody",
                            timestamp: "2025-08-31 07:33:50",
                          });
                          handleSubmitStatus();
                        }}
                        disabled={!isValid || statusIsSubmitting}
                      >
                        {statusIsSubmitting ? (
                          <span className="flex items-center">
                            <svg
                              className="-ml-1 mr-2 h-4 w-4 animate-spin text-white"
                              xmlns="http://www.w3.org/2000/svg"
                              fill="none"
                              viewBox="0 0 24 24"
                            >
                              <circle
                                className="opacity-25"
                                cx="12"
                                cy="12"
                                r="10"
                                stroke="currentColor"
                                strokeWidth="4"
                              ></circle>
                              <path
                                className="opacity-75"
                                fill="currentColor"
                                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                              ></path>
                            </svg>
                            Évaluation en cours...
                          </span>
                        ) : (
                          <span className="flex items-center">
                            <svg className="mr-2 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            Finaliser l'évaluation
                          </span>
                        )}
                      </button>
                    )}
                  </div>
                </div>
              ) : (
                // Autres contenus de modal (détails article, etc.)
                modalContent
              )}
            </Modal>
          </>
        )}
      </StatusHandler>
    </div>
  );
};

export default ArticlesPartage;